import { beneficialInterestLabel, formatShare, RespondentOwnerInput } from '@kanos/domain';
import { Button } from '@kanos/ui';
import {
  AddButton,
  checkForm,
  ConfirmDialog,
  FormDialog,
  QueryContent,
  useIdempotencyKey,
  useOwnerFields,
} from '@kanos/web-core';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import {
  ownersQuery,
  useRemoveOwner,
  useReplaceOwner,
  useStateOwner,
  type BeneficialOwner,
} from './intake-queries';

const nameOf = (owner: BeneficialOwner) => `${owner.givenName} ${owner.familyName}`;

/**
 * The beneficial owners the person states (§ 3 GwG, ADR-026): who stands behind the contracting
 * party. The firm checks them; what it records itself does not show here.
 */
export function OwnOwners({ caseId, isOpen }: { caseId: string; isOpen: boolean }) {
  const owners = useQuery(ownersQuery(caseId));
  const [editing, setEditing] = useState<BeneficialOwner | 'new' | null>(null);
  const [removing, setRemoving] = useState<BeneficialOwner | null>(null);
  const remove = useRemoveOwner(caseId);

  return (
    <div className="grid gap-4">
      <QueryContent query={owners}>
        {(list) =>
          list.length === 0 ? (
            <p className="text-sm text-muted">Noch niemand angegeben.</p>
          ) : (
            <ul className="divide-y divide-hairline border-y border-hairline">
              {list.map((owner) => (
                <li key={owner.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 py-3">
                  <div className="grid min-w-0 flex-1 basis-56 gap-0.5">
                    <p className="font-semibold">{nameOf(owner)}</p>
                    <p className="text-sm text-muted">
                      {beneficialInterestLabel[owner.interest]}
                      {owner.shareBasisPoints !== null &&
                        ` · ${formatShare(owner.shareBasisPoints)}`}
                    </p>
                  </div>
                  {isOpen && (
                    <div className="flex gap-2">
                      <Button variant="quiet" size="sm" onPress={() => setEditing(owner)}>
                        Ändern<span className="sr-only">: {nameOf(owner)}</span>
                      </Button>
                      <Button variant="quiet" size="sm" onPress={() => setRemoving(owner)}>
                        Entfernen<span className="sr-only">: {nameOf(owner)}</span>
                      </Button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )
        }
      </QueryContent>
      {isOpen && <AddButton label="Person hinzufügen" onPress={() => setEditing('new')} />}
      {editing && (
        <OwnerDialog
          caseId={caseId}
          owner={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
      {removing && (
        <ConfirmDialog
          title={`${nameOf(removing)} entfernen?`}
          description="Die Kanzlei sieht die Person danach nicht mehr als von Ihnen angegeben."
          confirmLabel="Entfernen"
          isPending={remove.isPending}
          error={remove.error}
          onConfirm={() => remove.mutate(removing.id, { onSuccess: () => setRemoving(null) })}
          onClose={() => setRemoving(null)}
        />
      )}
    </div>
  );
}

function OwnerDialog({
  caseId,
  owner,
  onClose,
}: {
  caseId: string;
  owner: BeneficialOwner | null;
  onClose: () => void;
}) {
  const add = useStateOwner(caseId);
  const replace = useReplaceOwner(caseId);
  const saving = owner ? replace : add;
  // The dialog closes after a success, so its one key never needs renewing.
  const [idempotencyKey] = useIdempotencyKey();
  const person = useOwnerFields(owner);
  const [unfit, setUnfit] = useState(false);

  const submit = () => {
    const checked = checkForm(RespondentOwnerInput, person.values);
    setUnfit(checked === null);
    if (!checked) return;
    if (owner) replace.mutate({ ownerId: owner.id, body: checked }, { onSuccess: onClose });
    else add.mutate({ body: checked, idempotencyKey }, { onSuccess: onClose });
  };

  return (
    <FormDialog
      title={owner ? `${nameOf(owner)} ändern` : 'Person hinzufügen'}
      description="Eine natürliche Person, die hinter Ihnen oder Ihrem Unternehmen steht. Wer im Einzelfall dazugehört, klärt die Kanzlei mit Ihnen."
      submitLabel={owner ? 'Speichern' : 'Hinzufügen'}
      isPending={saving.isPending}
      error={saving.error}
      unfit={unfit}
      onSubmit={submit}
      draft={person.values}
      onClose={onClose}
    >
      {person.fields}
    </FormDialog>
  );
}
